
import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { ApplicationError, TenderPlanLimitError, UnauthorizedError } from "../general/Errors";
import { tenderPlanLimitMessage } from "../tenderPlan";
import { useNavigate } from "react-router-dom";
import { currentPathForRedirect, loginPathWithTenant } from "../utilities/loginRedirect";
import { GlobalSidebar } from "./GlobalSidebar";

import { CheckCircle2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
/** Optional look of the confirmation dialog: the confirm button's text and whether it is destructive. */
export interface ConfirmationDialogOptions {
    confirmText?: string;
    destructive?: boolean;
}

type ModalContextType = {
    showDeleteDialog: any;
    showKeywordConfirmDialog: any;
    showConfirmationDialog: any;
    showMessageDialog: any;
    pushMessage: any;
    pushSuccess: any;
    pushError: any;
};

const ModalContext = createContext<ModalContextType | undefined>(undefined);

// A success is a confirmation you glance at; an error is text you have to read,
// so it gets longer on screen. Neither may stay forever: the popup is absolutely
// positioned over the page header, and an undismissed one hides it for good.
const SUCCESS_MESSAGE_TIMEOUT_MS = 5000;
const MESSAGE_TIMEOUT_MS = 15000;


export const ModalContextProvider = (props: any) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [deleteDialogTitle, setDeleteDialogTitle] = useState('');
    const [deleteDialogKeyword, setDeleteDialogKeyword] = useState('');
    const [deleteDialogCallback, setDeleteDialogCallback] = useState() as any;
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    // The delete dialog used to fire its callback and close in the same tick, so a failing delete
    // was an unhandled rejection behind an already-closed dialog: no toast, row still there (#1184).
    // Now the button waits for the callback, stays disabled meanwhile, and the dialog closes only
    // when the delete went through. A failure surfaces through pushError and leaves the dialog open.
    const [deleteDialogInFlight, setDeleteDialogInFlight] = useState(false);
    const deleteDialogInFlightRef = useRef(false);
    const [keywordDelete, setKeywordDelete] = useState('');
    const [keywordConfirmTitle, setKeywordConfirmTitle] = useState('');
    const [keywordConfirmMessage, setKeywordConfirmMessage] = useState<React.ReactNode>('');
    const [keywordConfirmKeyword, setKeywordConfirmKeyword] = useState('');
    const [keywordConfirmButtonText, setKeywordConfirmButtonText] = useState('');
    const [keywordConfirmInput, setKeywordConfirmInput] = useState('');
    const [keywordConfirmCallback, setKeywordConfirmCallback] = useState() as any;
    const [keywordConfirmOpen, setKeywordConfirmOpen] = useState(false);
    const [keywordConfirmInFlight, setKeywordConfirmInFlight] = useState(false);
    const keywordConfirmInFlightRef = useRef(false);
    const [confirmationDialogTitle, setConfirmationDialogTitle] = useState('');
    const [confirmationDialogMessage, setConfirmationDialogMessage] = useState('');
    const [confirmationDialogCallback, setConfirmationDialogCallback] = useState() as any;
    const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
    // The dialog fades out after the click; a second click on its button in that moment must not run the callback again.
    const confirmationHandled = useRef(false);
    const [confirmationHandledState, setConfirmationHandledState] = useState(false);
    const [confirmationDialogOptions, setConfirmationDialogOptions] = useState<ConfirmationDialogOptions>({});
    const [messageDialogOpen, setMessageDialogOpen] = useState(false);
    const [messageDialogTitle, setMessageDialogTitle] = useState('');
    const [messageDialogMessage, setMessageDialogMessage] = useState('');
    const [messageDialogOnClose, setMessageDialogOnClose] = useState<(() => void | Promise<void>) | undefined>(undefined);
    const [messageDialogIcon, setMessageDialogIcon] = useState<'success' | 'warning' | 'info' | undefined>(undefined);


    const showDeleteDialog = useCallback((title: string, keyword: string, deleteCallback: () => void | Promise<void>) => {
        setKeywordDelete('');
        setDeleteDialogInFlight(false);
        deleteDialogInFlightRef.current = false;
        setDeleteDialogTitle(title);
        setDeleteDialogKeyword(keyword);
        setDeleteDialogCallback(() => deleteCallback);
        setDeleteDialogOpen(true);
    }, []);

    const showKeywordConfirmDialog = useCallback((title: string, message: React.ReactNode, keyword: string, confirmButtonText: string, callback: () => void | Promise<void>) => {
        setKeywordConfirmInput('');
        setKeywordConfirmInFlight(false);
        keywordConfirmInFlightRef.current = false;
        setKeywordConfirmTitle(title);
        setKeywordConfirmMessage(message);
        setKeywordConfirmKeyword(keyword);
        setKeywordConfirmButtonText(confirmButtonText);
        setKeywordConfirmCallback(() => callback);
        setKeywordConfirmOpen(true);
    }, []);

    const showConfirmationDialog = useCallback((title: string, message: string, confirmationCallback: () => void, options: ConfirmationDialogOptions = {}) => {
        confirmationHandled.current = false;
        setConfirmationHandledState(false);
        setConfirmationDialogOptions(options);
        setConfirmationDialogTitle(title);
        setConfirmationDialogMessage(message);
        setConfirmationDialogCallback(() => confirmationCallback);
        setConfirmationDialogOpen(true);
    }, []);

    const showMessageDialog = useCallback((title: string, message: string, onClose?: () => void | Promise<void>, icon?: 'success' | 'warning' | 'info') => {
        setMessageDialogTitle(title);
        setMessageDialogMessage(message);
        setMessageDialogOnClose(onClose);
        setMessageDialogIcon(icon);
        setMessageDialogOpen(true);
    }, []);

    // Nutzerentscheid 2026-08-17: Meldungen laufen über sonner unten rechts
    // statt über das frühere Popup-Overlay. Fehler bleiben länger stehen.
    const pushMessage = useCallback((message: any) => {
        toast.error(message, { duration: MESSAGE_TIMEOUT_MS });
    }, []);

    const pushSuccess = useCallback((message: string) => {
        toast.success(message, { duration: SUCCESS_MESSAGE_TIMEOUT_MS });
    }, []);

    const errorTranslations: Record<string, string> = {
        'Cannot delete the last Customer Administrator. Assign the role to another user first.': t('errors.cannotDeleteLastAdmin'),
        'Cannot remove the last Customer Administrator. At least one Customer Administrator must remain.': t('errors.cannotRemoveLastAdmin'),
    };

    const pushError = useCallback((error: any) => {
        console.error(error);

        if (error instanceof TenderPlanLimitError) {
            // Not a failure but a plan boundary: a dialog that names the limit and the way up,
            // rather than an error toast the user would read as "something broke".
            const { title, message } = tenderPlanLimitMessage(error, t);
            showMessageDialog(title, message, undefined, 'warning');
        } else if (error instanceof ApplicationError) {
            pushMessage(errorTranslations[error.message] || error.toString());
        } else if (error instanceof UnauthorizedError) {
            navigate(loginPathWithTenant(currentPathForRedirect()));
        } else if (error.status === 401 && (error.passwordChangeRequired || error.mfaRequired)) {
            console.log('Authentication requirement error in pushError:', error);
            navigate(loginPathWithTenant(currentPathForRedirect()));
        } else {
            pushMessage(t('global.generalError'));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate, pushMessage, showMessageDialog, t]);

    const contextValue = useMemo(() => ({
        showDeleteDialog, showKeywordConfirmDialog, showConfirmationDialog, showMessageDialog, pushMessage, pushSuccess, pushError
    }), [showDeleteDialog, showKeywordConfirmDialog, showConfirmationDialog, showMessageDialog, pushMessage, pushSuccess, pushError]);

    return (
        <ModalContext.Provider value={contextValue}>
            <div className="positionRelative">
                {props.children}
                <GlobalSidebar />
                <Dialog
                    open={deleteDialogOpen}
                    onOpenChange={(open) => {
                        if (open) return;
                        if (deleteDialogInFlightRef.current) return;
                        setDeleteDialogOpen(false);
                    }}
                >
                    <DialogContent
                        className="w-[calc(100vw-2rem)] sm:max-w-[600px]"
                        onInteractOutside={(event) => event.preventDefault()}
                    >
                        <DialogHeader>
                            <DialogTitle>{deleteDialogTitle}</DialogTitle>
                        </DialogHeader>
                        <div>
                            <div><Trans i18nKey="global.deleteDialogInstruction" values={{ keyword: deleteDialogKeyword }} components={{ 1: <span className='font-bold' /> }} /></div>
                            <Input className="mt-3" aria-label={deleteDialogKeyword} value={keywordDelete || ''} onChange={(event) => setKeywordDelete(event.target.value || '')} />
                        </div>
                        <DialogFooter className="gap-2 pt-4 sm:gap-0">
                            <Button variant="outline" type="button" disabled={deleteDialogInFlight} onClick={() => {
                                setDeleteDialogOpen(false);
                            }}>{t('global.cancel')}</Button>
                            <Button variant="destructive" type="button" disabled={deleteDialogInFlight || deleteDialogKeyword.replace(/\s/g, '') !== keywordDelete.replace(/\s/g, '')} onClick={async () => {
                                if (deleteDialogInFlightRef.current) return;
                                deleteDialogInFlightRef.current = true;
                                setDeleteDialogInFlight(true);
                                try {
                                    await deleteDialogCallback();
                                    setDeleteDialogOpen(false);
                                } catch (error) {
                                    pushError(error);
                                } finally {
                                    deleteDialogInFlightRef.current = false;
                                    setDeleteDialogInFlight(false);
                                }
                            }}>{t('global.delete')}</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
                <Dialog
                    open={keywordConfirmOpen}
                    onOpenChange={(open) => {
                        if (open) return;
                        if (keywordConfirmInFlightRef.current) return;
                        setKeywordConfirmOpen(false);
                    }}
                >
                    <DialogContent
                        className="w-[calc(100vw-2rem)] sm:max-w-[600px]"
                        onInteractOutside={(event) => event.preventDefault()}
                    >
                        <DialogHeader>
                            <DialogTitle>{keywordConfirmTitle}</DialogTitle>
                        </DialogHeader>
                        <div>
                            <div>{keywordConfirmMessage}</div>
                            <div className='mt-5'>
                                <Trans i18nKey="global.keywordConfirmInstruction" values={{ keyword: keywordConfirmKeyword }} components={{ 1: <span className='font-bold' /> }} />
                            </div>
                            <Input className="mt-3" aria-label={keywordConfirmKeyword} value={keywordConfirmInput} onChange={(event) => setKeywordConfirmInput(event.target.value ?? '')} />
                        </div>
                        <DialogFooter className="gap-2 pt-4 sm:gap-0">
                            <Button variant="outline" type="button" disabled={keywordConfirmInFlight} onClick={() => setKeywordConfirmOpen(false)}>{t('global.cancel')}</Button>
                            <Button type="button" disabled={keywordConfirmInFlight || keywordConfirmKeyword !== keywordConfirmInput.trim()} onClick={async () => {
                                if (keywordConfirmInFlightRef.current) return;
                                keywordConfirmInFlightRef.current = true;
                                setKeywordConfirmInFlight(true);
                                try {
                                    await keywordConfirmCallback();
                                } finally {
                                    keywordConfirmInFlightRef.current = false;
                                    setKeywordConfirmInFlight(false);
                                    setKeywordConfirmOpen(false);
                                }
                            }}>{keywordConfirmButtonText}</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
                <Dialog
                    open={confirmationDialogOpen}
                    onOpenChange={(open) => {
                        if (!open) {
                            setConfirmationDialogOpen(false);
                        }
                    }}
                >
                    <DialogContent
                        className="w-[calc(100vw-2rem)] sm:max-w-[600px]"
                        onInteractOutside={(event) => event.preventDefault()}
                    >
                        <DialogHeader>
                            <DialogTitle>{confirmationDialogTitle}</DialogTitle>
                        </DialogHeader>
                        <div>
                            {/* {properties.text ?
                <div>{properties.text}</div>
                :
                <></>
            } */}
                            <div>{confirmationDialogMessage}</div>
                        </div>
                        <DialogFooter className="gap-2 pt-4 sm:gap-0">
                            <Button variant="outline" type="button" onClick={() => {
                                setConfirmationDialogOpen(false);
                            }}>{t('global.cancel')}</Button>
                            <Button
                                type="button"
                                variant={confirmationDialogOptions.destructive ? 'destructive' : 'default'}
                                disabled={confirmationHandledState}
                                onClick={() => {
                                    if (confirmationHandled.current) return;
                                    confirmationHandled.current = true;
                                    setConfirmationHandledState(true);
                                    confirmationDialogCallback();
                                    setConfirmationDialogOpen(false);
                                }}
                            >{confirmationDialogOptions.confirmText ?? t('global.confirm')}</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
                <Dialog
                    open={messageDialogOpen}
                    onOpenChange={async (open) => {
                        if (open) return;
                        const onClose = messageDialogOnClose;
                        setMessageDialogOnClose(undefined);
                        setMessageDialogIcon(undefined);
                        setMessageDialogOpen(false);
                        if (typeof onClose === 'function') await onClose();
                    }}
                >
                    <DialogContent
                        className="w-[calc(100vw-2rem)] sm:max-w-[600px]"
                        onInteractOutside={(event) => event.preventDefault()}
                    >
                        <DialogHeader>
                            <DialogTitle>{messageDialogTitle}</DialogTitle>
                        </DialogHeader>
                        <div className='flex items-center gap-4'>
                            {messageDialogIcon === 'success' && (
                                <CheckCircle2 className="h-12 w-12 shrink-0 text-success" aria-hidden />
                            )}
                            <div className="min-w-0 flex-1">{messageDialogMessage}</div>
                        </div>
                        <DialogFooter className="pt-4">
                            <Button type="button" onClick={async () => {
                                const onClose = messageDialogOnClose;
                                setMessageDialogOnClose(undefined);
                                setMessageDialogIcon(undefined);
                                setMessageDialogOpen(false);
                                if (typeof onClose === 'function') await onClose();
                            }}>{t('global.ok')}</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </ModalContext.Provider>
    )
};

export const useModalContext = () => useContext(ModalContext);